import { FC, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useParams, useSearchParams } from 'react-router';
import dayjs from 'dayjs';
import {
  colors,
  fontFamilies,
  fontSizes,
  fontWeights,
  rem,
} from '../../theme/tokens';

// Public verification (L2-VQR).
//
// Anyone holding a printed document can scan its QR code and land here, without an
// account. The page therefore never reads the session, never offers a way into the
// application, and shows only the fields the client listed.
//
// The route is exempt from the redirect in App.tsx, which would otherwise send an
// anonymous visitor to /login and defeat the whole point.
//
// Own design (no screen in the XD — DESIGN.md, gap 3), deliberately unlike every other
// screen in the product. It used to reuse BrandScreen, the blue backdrop Login and the
// password screens share, which made a verification result look like a sign-in prompt:
// the one reading it has no account and is not being asked for one. So:
//   - The application's own grey background, not the brand blue. Nothing here is a way
//     into the application, and the two should not be mistaken for each other.
//   - The verdict is the page. It is set in the serif, large, and carries the only
//     colour: blue for authentic, orange for a document that is not in the archive.
//     Everything under it is deliberately plain.
//   - Facts are a description list: label above value, ruled, aligned on one margin.
//     A document's provenance reads like a record, not like a form to fill in.
//   - No logo lockup, no brand title, no decorative circles. Someone is checking a
//     piece of paper, not visiting a website.

type VerifiedDocument = {
  reference: string;
  issuing_date: string | null;
  issuing_authority: string | null;
  archived_at: string | null;
};

// The server already answers in the client's own words, so its message is displayed as
// it comes. These stand in when the request never reaches it.
const UNREACHABLE_MESSAGE =
  'Le service de vérification est momentanément indisponible. Veuillez réessayer ultérieurement.';
const MISSING_CODE_MESSAGE =
  "Le document présenté n'a pas été trouvé dans le système de vérification";

const formatDate = (value: string | null) =>
  value ? dayjs(value).format('DD/MM/YYYY') : '—';

// Label above value rather than beside it: the reference is a UUID, and a value column
// narrow enough to sit next to a label would break it across three lines on a phone —
// which is the device this page is read on.
const Field: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box
    component="div"
    sx={{ py: 2, borderTop: `1px solid ${colors.border}` }}
  >
    <Typography
      component="dt"
      sx={{
        color: colors.textBase,
        opacity: 0.6,
        fontSize: rem(fontSizes.sm),
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      component="dd"
      sx={{
        m: 0,
        color: colors.textBase,
        fontSize: rem(fontSizes.md),
        fontWeight: fontWeights.medium,
        overflowWrap: 'anywhere',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const Verify: FC = () => {
  const { code } = useParams();
  // Set by the scan redirect: that consultation is already in the journal.
  const scanId = useSearchParams()[0].get('scan');
  const [document_, setDocument] = useState<VerifiedDocument>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.document.title = 'ArchivOs - Vérification';
  }, []);

  useEffect(() => {
    if (!code) {
      setErrorMessage(MISSING_CODE_MESSAGE);
      setIsLoading(false);
      return;
    }

    // The scan redirect sends unresolved codes here, to a fixed word in place of a
    // reference: it has already asked the question, and re-asking it would put the code
    // that missed back in the address bar. These two are answers, not codes.
    if (code === 'no-match') {
      setErrorMessage(MISSING_CODE_MESSAGE);
      setIsLoading(false);
      return;
    }

    if (code === 'unavailable') {
      setErrorMessage(UNREACHABLE_MESSAGE);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    // Deliberately not archivosFetch: that helper attaches the bearer token, and this
    // page is meant to work without one.
    //
    // The call is same-origin rather than aimed at REACT_APP_API_URL: the address is
    // baked in at build time and points at the canonical host, which is wrong for
    // anyone reaching the page through another one. A printed QR code can be scanned
    // from anywhere, so the request follows the page instead.
    const query = scanId ? `?scan=${encodeURIComponent(scanId)}` : '';

    fetch(`/api/verify/${encodeURIComponent(code)}${query}`)
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (response.ok && body.found) {
          setDocument(body.document);
        } else {
          // 404, 429 and 503 all carry a message written for the reader.
          setErrorMessage(body.message || UNREACHABLE_MESSAGE);
        }
      })
      .catch(() => {
        // A network failure must read like the service being unavailable, not like a
        // document that does not exist.
        if (!cancelled) setErrorMessage(UNREACHABLE_MESSAGE);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [code, scanId]);

  return (
    <Box
      sx={(theme) => ({
        minHeight: '100vh',
        backgroundColor: colors.surface,
        color: colors.textBase,
        display: 'flex',
        justifyContent: 'center',
        px: 3,
        py: 10,
        [theme.breakpoints.down('tablet')]: { px: 2.5, py: 6 },
      })}
    >
      <Box sx={{ width: '100%', maxWidth: 560 }}>
        {isLoading && (
          <Box data-testid="verify-loading" sx={{ py: 4 }}>
            <CircularProgress color="primary" size={28} />
          </Box>
        )}

        {!isLoading && errorMessage && (
          <Typography
            data-testid="verify-error"
            role="alert"
            component="h1"
            sx={(theme) => ({
              fontFamily: fontFamilies.serif,
              fontWeight: fontWeights.bold,
              // Well under the 60px display step: this is a sentence, not a brand
              // title, and it has to hold on a phone held at arm's length.
              fontSize: rem(36),
              lineHeight: 1.15,
              color: colors.accent,
              m: 0,
              [theme.breakpoints.down('tablet')]: { fontSize: rem(28) },
            })}
          >
            {errorMessage}
          </Typography>
        )}

        {!isLoading && document_ && (
          <Box data-testid="verify-result">
            <Typography
              component="h1"
              sx={(theme) => ({
                fontFamily: fontFamilies.serif,
                fontWeight: fontWeights.bold,
                fontSize: rem(44),
                lineHeight: 1.1,
                color: colors.primary,
                m: 0,
                mb: 5,
                [theme.breakpoints.down('tablet')]: { fontSize: rem(32) },
              })}
            >
              Ce document est authentique.
            </Typography>

            <Box component="dl" sx={{ m: 0 }}>
              <Field
                label="Date d'émission"
                value={formatDate(document_.issuing_date)}
              />
              <Field
                label="Autorité émettrice"
                value={document_.issuing_authority || '—'}
              />
              <Field label="Référence" value={document_.reference} />
              <Field
                label="Date d'archivage"
                value={formatDate(document_.archived_at)}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Verify;
